import { createMemo, createSignal, Show } from "solid-js";
import { useNavigate } from "@solidjs/router";
import { amendmentCount, padId, stageCount } from "~/lib";
import { runDeleteCommand } from "~/_server";
import { useToast } from "~/_context/ToastProvider";

import { SquarePen, Printer, Trash2 as Delete } from "lucide-solid";

export const Certificate = ( props: any ) => {
	const navigate = useNavigate();
	const { addToast, toastTypes } = useToast();
	const [deleteable, setDeleteable] = createSignal<boolean>( false );
	const [deleted, setDeleted] = createSignal<boolean>( false );

	if ( typeof props.certificate == "undefined" )
		return;

	const certificate = createMemo( () =>
		typeof props.certificate === "function"
			? props.certificate()
			: props.certificate
	);

	const memo = createMemo( () => {
		const isDraft = certificate().status === "draft";
		const isPaid = certificate().status === "paid";
		const isPublished = certificate().status === "published" || isPaid;

		const stage = props.full ? "Stage " : "S";
		const amendment = props.full ? "Amendment " : "A";

		const stageCnt = stageCount( certificate() );
		const amendCount = amendmentCount( certificate() );

		let link = certificate().riasId ? padId( certificate().riasId ) : "";

		const base = {
			projectId: certificate().parent.replace( 'PROJECT#', '' ),
			certificateId: null,
			amendment: 0,
			stage: 0,
		};

		// Re-opening a draft reuses the wizard it was started in, so it is described
		// by the stage/amendment it already is rather than the next one along.
		const state = { ...base, stage: stageCnt, amendment: amendCount };

		// Follow-ons are copied from this record. A new stage starts a fresh run of
		// amendments; an amendment stays on the stage it is amending, which is what
		// lets Stage 1 be amended after Stage 2 has been issued.
		const stageState = { ...base, certificateId: certificate().sk, stage: stageCnt + 1 };
		const amendState = { ...base, certificateId: certificate().sk, stage: stageCnt, amendment: amendCount + 1 };

		if ( stageCnt ) link += `-Stage${stageCnt}`;

		if ( amendCount ) link += `-Amendment${amendCount}`;

		return { state, stageState, amendState, stage, amendment, stageCount: stageCnt, amendCount, link, isPaid, isPublished, isDraft };
	} )

	/*
	href={`/certificate/C${memo().link}`}*/

	return (
		<div class="cert-options group inline-flex flex-wrap max-w-full md:flex-nowrap md:max-w-none items-center justify-between gap-x-1.5 gap-y-1 mr-2 my-1 relative rounded-md bg-gray-100 px-2 py-1 font-medium text-gray-600 hover:bg-primary hover:text-white transition-colors duration-300"
			classList={{ "text-xl md:text-3xl": !props.slimView, "text-lg": props.slimView }}
		>
			<svg viewBox="0 0 12 12" aria-hidden="true" class="size-3.5 mx-1 mt-1.5"
				classList={{
					"fill-red-400": !memo().isPublished,
					"fill-yellow-400": memo().isPublished && !memo().isPaid,
					"fill-green-400": memo().isPaid,
					"opacity-20": deleted(),
				}}>
				<circle r="5" cx="5" cy="5" />
			</svg>

			<span class="mr-3 md:mr-8 text-nowrap">
				C{padId( props.certificate?.riasId )}
				{memo().stageCount ? ` - ${memo().stage}${memo().stageCount}` : ""}
				{memo().amendCount ? ` - ${memo().amendment}${memo().amendCount}` : ""}
			</span>

			{/*}
			<Show when={props.certificate?.status == "draft" && props.full === true}>
				<div
					onClick={async () => {
						if ( !deleteable() ) {
							setDeleteable( true );
						} else {
							const r = await runDeleteCommand(
								props.certificate.pk,
								props.certificate.sk,
							);

							if ( !r.statusCode || r.statusCode == -1 ) {
								addToast( r.error ?? "Delete failed", toastTypes.error );
							} else {
								setDeleted( true );
								addToast( "Certificate Deleted", toastTypes.success );
								props.revalidate();
							}
						}
					}}
					class="transition-all left-1 group-hover:left-4/5 group-hover:z-15 absolute cursor-pointer border top-0 h-full rounded-md group-hover:rounded-l-none  pl-8 pr-2 py-1 text-lg font-medium "
					classList={{
						"bg-gray-500": !deleteable(),
						"bg-white": deleteable(),
						"border-gray-500": !deleteable(),
						"border-red-700": deleteable(),
						"text-white": !deleteable(),
						"text-red-700": deleteable(),
					}}>
					<Trash height="auto" width="20px" />
				</div>
			</Show>
			*/}

			<Show when={memo().isDraft}>
				<span class="icon" title="Edit Certificate" onClick={() => navigate( `/dashboard/certificate/${certificate().sk.replace( 'CERTIFICATE#', '' )}`, { state: memo().state } )}>
					<SquarePen height="auto" width="30px" />
				</span>
			</Show>

			<span class="icon">
				<a href={`/certificate/C${memo().link}?print=true`} target="_blank" rel="noopener noreferrer" title="Print Certificate">
					<Printer height="auto" width="30px" />
				</a>
			</span>

			<Show when={memo().isDraft && props.full === true}>
				<span class="icon" title="Delete Certificate"
					onClick={async () => {
						if ( !deleteable() ) {
							setDeleteable( true );

							setTimeout( () => {
								setDeleteable( false );
							}, 3000 );
						} else {
							const r = await runDeleteCommand(
								certificate().pk,
								certificate().sk,
							);

							if ( !r.statusCode || r.statusCode == -1 ) {
								addToast( r.error ?? "Delete failed", toastTypes.error );
							} else {
								setDeleted( true );
								addToast( "Certificate Deleted", toastTypes.success );
								props.revalidate();
							}
						}
					}}>
					<Delete height="auto" width="30px" classList={{"hover:stroke-red-300":deleteable()}} />
				</span>
			</Show>

			<Show when={memo().isPaid && props.canAddStage}>
				<span class="icon text-sm whitespace-nowrap" title={`Add Stage ${memo().stageCount + 1}`} onClick={() => navigate( `/dashboard/certificate/new`, { state: memo().stageState } )}>+Stage</span>
			</Show>

			<Show when={memo().isPaid && props.canAmend}>
				<span class="icon text-sm whitespace-nowrap" title={memo().stageCount ? `Add Amendment ${memo().amendCount + 1} to Stage ${memo().stageCount}` : "Add Amendment"} onClick={() => navigate( `/dashboard/certificate/new`, { state: memo().amendState } )}>+Amendment</span>
			</Show>
		</div>
	);
};
